import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import TrueImpactSimpleModal from 'shared/design-system/components/TrueImpactSimpleModal';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactTextIndicator from 'shared/design-system/components/TrueImpactTextIndicator';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactAlertIcon from 'shared/design-system/components/TrueImpactAlertIcon';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';

const DEFAULT_MAX_NOTE_LENGTH = 500;

const StyledDiv = styled.div`
  padding: ${SPACERS.large} ${SPACERS.medium} ${SPACERS.medium};
  .icon-row {
    display: flex;
    justify-content: center;
    margin-bottom: ${SPACERS.medium};
  }
  .message {
    margin: 0;
    line-height: 1.7;
  }
  .note-wrapper {
    margin-top: ${SPACERS.medium};
  }
`;

const ErrorMessage = styled(TrueImpactText)`
  margin-top: ${SPACERS.tiny};
`;

interface TrueImpactLevelFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (note: string) => void;
  message: React.ReactNode;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
  initialNote?: string;
  maxLength?: number;
  disableConfirmIfEmpty?: boolean;
  isSaving?: boolean;
  errorMessage?: string;
}

const TrueImpactLevelFeedbackModal: React.FC<TrueImpactLevelFeedbackModalProps> = ({
  open,
  onClose,
  onConfirm,
  message,
  confirmText = 'Save',
  cancelText = 'Cancel',
  placeholder = 'Please provide a brief explanation.',
  initialNote = '',
  maxLength = DEFAULT_MAX_NOTE_LENGTH,
  disableConfirmIfEmpty = true,
  isSaving = false,
  errorMessage,
}) => {
  const [note, setNote] = useState(initialNote);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setNote(initialNote);
    }
  }, [open, initialNote]);

  const handleNoteMouseDownCapture = useCallback(() => {
    textareaRef.current?.focus();
  }, []);

  const isEmpty = note.trim() === '';
  const isTooLong = note.length > maxLength;

  return (
    <TrueImpactSimpleModal
      open={open}
      onClose={onClose}
      contentStyle={{
        maxWidth: 480,
        borderRadius: 4,
        overflow: 'hidden',
      }}
      next={{
        text: confirmText,
        onClick: () => onConfirm(note),
        disabled: (disableConfirmIfEmpty && isEmpty) || isTooLong || isSaving,
      }}
      previous={{
        text: cancelText,
        onClick: onClose,
      }}
    >
      <StyledDiv>
        <div className="icon-row">
          <TrueImpactAlertIcon
            icon={faTriangleExclamation}
            color="warningDark"
            shadowOpacity={0.15}
          />
        </div>
        <div className="message">{message}</div>
        <div
          className="note-wrapper"
          onMouseDownCapture={handleNoteMouseDownCapture}
        >
          <TrueImpactTextfield
            isTextarea
            inputRef={textareaRef}
            placeholder={placeholder}
            value={note}
            onChange={setNote}
            maxLength={maxLength}
          />
          <TrueImpactTextIndicator
            minLength={0}
            text={note}
            maxLength={maxLength}
          />
          {errorMessage && (
            <ErrorMessage variant="body-small" color="critical">
              {errorMessage}
            </ErrorMessage>
          )}
        </div>
      </StyledDiv>
    </TrueImpactSimpleModal>
  );
};

export default TrueImpactLevelFeedbackModal;
